import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImg } from "@/lib/api";

export default function ProgrammeGallery({ items = [] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: items.length > 1, align: "start" });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (items.length === 0) return null;

  return (
    <div data-testid="programme-gallery" className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {items.map((item, i) => (
            <figure key={`${item.url}-${i}`} data-testid="gallery-slide" className="relative min-w-0 flex-[0_0_100%]">
              <div className="aspect-[16/10] overflow-hidden bg-ptf-primary/10">
                <img
                  src={resolveImg(item.url)}
                  alt={item.caption || "Programme photo"}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  draggable="false"
                />
              </div>
              {item.caption && (
                <figcaption
                  data-testid="gallery-caption"
                  className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ptf-primary/90 via-ptf-primary/50 to-transparent px-5 pb-4 pt-14 text-sm text-ptf-bg"
                >
                  {item.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>

      {items.length > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-primary/50">
            <span data-testid="gallery-counter">{String(selected + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</span>
            <span className="ml-3 text-ptf-primary/35 sm:hidden">Swipe</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous photo"
              data-testid="gallery-prev"
              className="grid h-10 w-10 place-items-center border border-ptf-primary/20 text-ptf-primary hover:bg-ptf-primary hover:text-ptf-bg transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next photo"
              data-testid="gallery-next"
              className="grid h-10 w-10 place-items-center border border-ptf-primary/20 text-ptf-primary hover:bg-ptf-primary hover:text-ptf-bg transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
